<template>
  <div id="import-calculator" class="container">
    <div class="title">Рассчитайте стоимость доставки</div>
    <div class="left-block">
      <div class="row">
        <SelectInput :options="cities"
                     v-model="selected.country"
                     @update:modelValue="selected.storage = selected.country?.storages?.[0]"
                     label="Страна склада"/>
        <div class="form-field">
          <div class="form-label">
            Город получателя
          </div>
          <SelectInputRemote
              v-model="selected.city"
              :remote-method="CitiesSearch"
              placeholder="Введите название"
          />
        </div>
      </div>
      <div class="row">
        <div class="form-field">
          <div class="form-label">
            Вес, кг
          </div>
          <FloatInput placeholder="Укажите вес" class="form-input isset" v-model="selected.weight" :min="0" :max="maxWeight"/>
        </div>
        <div class="form-field">
          <div class="form-label">
            Способ доставки
          </div>
          <div>
            <RadioInput
                :variants="[{title:'До отделения', value:0},{title:'Курьером', value:1}]"
                v-model="selected.deliveryType"
            >
            </RadioInput>
          </div>
        </div>
      </div>
      <div v-if="selected.country?.storages?.some(storage => storage.volume_weight_enabled === 1)" class="row">
        <div class="form-field small">
          <div class="form-label">
            Длина, см
          </div>
          <FloatInput placeholder="Введите длину" class="form-input isset" v-model="selected.depth" :min="0" :max="120"/>
        </div>
        <div class="form-field small">
          <div class="form-label">
            Ширина, см
          </div>
          <FloatInput placeholder="Введите ширину" class="form-input isset" v-model="selected.width" :min="0" :max="80"/>
        </div>
        <div class="form-field small">
          <div class="form-label">
            Высота, см
          </div>
          <FloatInput placeholder="Введите высоту" class="form-input isset" v-model="selected.height" :min="0" :max="50"/>
        </div>
      </div>
      <div class="row">
        <div class="form-label" style="margin-top: -10px!important; font-size:14px;white-space: normal;">
          Максимальные габариты 120х80х50 см, максимальный вес {{ maxWeight }} кг<br>Учитывается <a class="blue" style="border-bottom:unset" href="/" target="_blank">объёмный вес</a>
        </div>
      </div>
      <div class="row inline" v-if="canAlt">
        <SwitchInput
            v-model="switchOn"
            label="Уточнить категорию товаров для более точного расчета"
        />
        <pop-over
            :width="250"
            content='Стоимость и способ доставки зависит от категорий товаров в посылке. Подробнее про маршруты и их ограничения вы можете узнать по <a class="blue" style="border-bottom:unset" href="https://int.bxb.delivery/news/alt/">ссылке</a>.'
        >
      </pop-over>
      </div>
      <div class="row" v-show="switchOn && canAlt">
        <SelectInputMultiple
            :options="categories"
            v-model="selectedCategories"
            label="Укажите категории всех товаров, которые планируете отправить в посылке"
        >
          <template #selectedOption="{ option }">
            <div :class="'selected-option '+ (option.routes?.[selected.storage?.storage_code]?.includes('alt')?'alt':'')"
                :key="option.id" >
              <span class="selected-option-title">
                {{option.title}}
              </span>
              <span class="selected-option-cross" :data-id="option.id">×</span>
            </div>
          </template>
        </SelectInputMultiple>
      </div>
      <div class="row" v-show="switchOn && canAlt && hasAlt">
        <div class="form-label red font13" style="margin-top: -10px!important;white-space: normal">
          Выделенные категории товаров доступны к отправке только альтернативным способом
        </div>
      </div>
    </div>
    <div class="right-block">
      <div 
        v-for="block in calcBlocks" 
        :key="`${block.storageCode || ''}-${block.route}`"
        class="calc-block blue"
      >
        <div class="bold pb5">{{ block.title }}</div>
        <div class="font14">{{ block.description }}</div>
        <div class="price">
          <div class="loader" v-show="loading"></div>
          <span class="extra-bold nowrap">
            <span v-show="!loading">
              {{ promoCode && selected.weight >= minWeight ? formatNumberWithDiscount(getBlockPrice(block)) : formatNumber(getBlockPrice(block)) }}
            </span>
            руб.<span v-if="shouldShowAsterisk()">* </span>
          </span>
          <span 
            :class="['strike', 'nowrap',{
              'font20':showTwoBlocks
            }]"
            v-show="promoCode && !loading && getBlockPrice(block) && selected.weight >= minWeight"
          >
            {{ formatNumber(getBlockPrice(block)) }} руб.
          </span>
          <div 
            class="blue font14" 
            v-show="promoCode && block.showDiscountInBlock && price && selected.weight >= minWeight"
            v-html="formatPromoDesc(promoCode?.desc2)"
          ></div>
          <div 
            class="blue font14" 
            v-show="promoCode && block.showDiscountInBlock && price && selected.weight < minWeight"
            v-html="formatPromoDesc(promoCode?.desc1)"
          ></div>
        </div>
        <div class="font16 bold">{{ getBlockTime(block) }}</div>
        <div class="font14">{{ getBlockDescription(block) }}</div>
      </div>

      <div class="blue font14" v-show="showDiscountNote" v-html="'*' + formatPromoDesc(promoCode?.desc2)"></div>
      <div class="blue font14" v-show="showDiscountInfo" v-html="formatPromoDesc(promoCode?.desc1)"></div>
      <a class="btn-red font18" href="https://my.bxb.delivery/ebay">Оформить отправку <span class="only-big-screen">в личном кабинете</span></a>
      <span class="blue font14"> Стоимость не включает пошлины и дополнительные услуги. Срок доставки рассчитан в рабочих днях с момента отправки посылки со склада. В зависимости от сезона сроки доставки могут быть увеличены. </span>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, computed, watch} from 'vue'
import axios from "axios";

import FloatInput from "@/components/FloatInput.vue";
import SelectInput from "@/components/SelectInput.vue";
import RadioInput from "@/components/RadioInput.vue";
import SwitchInput from "@/components/SwitchInput.vue";
import SelectInputRemote from "@/components/SelectInputRemote.vue";
import SelectInputMultiple from "@/components/SelectInputMultiple.vue";
import PopOver from "@/components/PopOver.vue";
import config from "@/config";


const cities = ref([])
const price = ref(null)
const categories = ref([{}])
const switchOn = ref(false)
const loading = ref(false)
const selected = ref({
  country: {},
  storage: {},
  city: {},
  weight: 0,
  deliveryType: 0,
  width: 0,
  height: 0,
  depth: 0,
})
const selectedCategories = ref([])

// Получаем константу PromoCode из window (передается из Yii2)
// Валидируем константу при получении
function validatePromoCode(promo) {
  if (!promo || typeof promo !== 'object') return null
  
  // Проверяем обязательные поля
  if (!promo.discount || typeof promo.discount !== 'number' || promo.discount <= 0) return null
  
  // Проверяем опциональные поля, если они есть
  if (promo.minWeight !== undefined && (typeof promo.minWeight !== 'number' || promo.minWeight < 0)) return null
  if (promo.desc1 !== undefined && typeof promo.desc1 !== 'string') return null
  if (promo.desc2 !== undefined && typeof promo.desc2 !== 'string') return null
  
  return promo
}

const promoCode = validatePromoCode(window.PROMO_CODE)

// Минимальный вес для применения скидки
const minWeight = computed(() => {
  return promoCode ? (promoCode.minWeight || 0) : 0
})

const maxWeight = computed(() => {
  return selected.value.deliveryType === 0 ? 15 : 31
})
const canAlt = computed(() => {
  return categories.value.some((category) => category.routes?.[selected.value.storage?.storage_code]?.includes('alt'))
})
const hasAlt = computed(() => {
  if (selectedCategories.value.length === 0 || !switchOn.value) return null
  return selectedCategories.value.some((category) => category.routes?.[selected.value.storage?.storage_code]?.includes('alt'))
})

// Показывать ли 2 блока (для Германии или когда есть alt маршрут, но категории не выбраны)
const showTwoBlocks = computed(() => {
  return canAlt.value && hasAlt.value === null
})

// Показывать ли сноску внизу (когда 2 блока)
const showDiscountNote = computed(() => {
  return promoCode && showTwoBlocks.value && price.value && selected.value.weight >= minWeight.value
})

const showDiscountInfo = computed(() => {
  return promoCode && showTwoBlocks.value && price.value && selected.value.weight < minWeight.value
})

// Получить список блоков для отображения
const calcBlocks = computed(() => {
  const blocks = []
    // Для остальных стран
    if (showTwoBlocks.value) {
      // Показываем оба блока
      blocks.push({
        title: 'Стандартный способ',
        description: 'Подходит для одежды, БАДов и других товаров.',
        route: 'direct',
        showDiscountInBlock: false
      })
      blocks.push({
        title: 'Альтернативный способ',
        description: 'Подходит для техники, косметики и других товаров.',
        route: 'alt',
        showDiscountInBlock: false
      })
    } else {
      // Показываем один блок
      const route = hasAlt.value === true ? 'alt' : 'direct'
      blocks.push({
        title: route === 'alt' ? 'Альтернативный способ' : 'Стандартный способ',
        description: route === 'alt' 
          ? 'Подходит для техники, косметики и других товаров.'
          : 'Подходит для одежды, БАДов и других товаров.',
        route: route,
        showDiscountInBlock: true
      })
    }
  
  return blocks
})

// Получить цену для блока
function getBlockPrice(block) {
  return price.value?.routes?.[block.route]?.cost
}

// Получить время для блока
function getBlockTime(block) {
  const route = block.route
  return price.value?.routes?.[route]?.time ?? (route === 'alt' ? 'от 12 рабочих дней' : 'от 7 рабочих дней')
}

// Получить описание для блока
function getBlockDescription(block) {
  return price.value?.routes?.[block.route]?.description ?? 'с момента отгрузки посылки со склада'
}

// Нужно ли показывать звездочку (скидка для веса >= minWeight при 2 блоках)
function shouldShowAsterisk() {
  if (!price.value || !promoCode) return false
  return !!(showTwoBlocks.value && selected.value.weight >= minWeight.value);
}

// Форматирование описания промокода с подстановкой переменных
function formatPromoDesc(template) {
  if (!promoCode || !template) return ''
  return template
    .replace(/\$\{discount\}/g, promoCode.discount)
    .replace(/\$\{minWeight\}/g, minWeight.value)
}

function formatNumber(value) {
  if(!value) return '-'
  return parseFloat(value.replace(/\s/g, '')).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })??'-'
}
function formatNumberWithDiscount(value) {
  if(!value || !promoCode) return '-'
  const val = parseFloat(value.replace(/\s/g, '')) - promoCode.discount
  return val.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })??'-'
}
watch(
    () => selected,
    async () => {
      if (selected.value.city?.id && selected.value.storage?.id && selected.value.weight) {
        loading.value = true
        await axios.get(config.apiHost+'/int-import-api/calculate/', {
          params: {
            'cityCode': selected.value.city.id,
            'storage_id': selected.value.storage.id,
            'weight': selected.value.weight,
            'courier': selected.value.deliveryType,
            'width': selected.value.width * 10 || undefined,
            'height': selected.value.height * 10 || undefined,
            'depth': selected.value.depth * 10 || undefined
          }
        }).then(response => {
          price.value = response.data;
        }).catch(error => {
          console.error("Ошибка при получении цены:", error);
        })
      }else{
        price.value = null
      }
      loading.value = false

    },
    {deep: true},
)

async function CitiesSearch(q) {
  let cities = []
  try {
    const response = await axios.get(config.apiHost+'/int-import-api/get-cities-list/', {params: {q: q}});
    cities = response.data;
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error);
  }
  return cities
}

onMounted(() => {
  axios
      .get(config.apiHost+'/int-import-api/get-countries/')
      .then((response) => {
        if (response.data) {
          cities.value = response.data
        } else {
          console.log(response);
        }
      }).catch((error) => {
        console.log(error);
      })
  axios
      .get(config.apiHost+'/int-import-api/get-categories/')
      .then((response) => {
        if (response.data) {
          categories.value = response.data
        } else {
          console.log(response);
        }
      })
      .catch((error) => {
        console.log(error);
      });
})

</script>

<style>
@media only screen and (max-width: 480px) {
  .only-big-screen {
    display: none !important;
  }
  #import-calculator .title{
    font-size: 22px !important;
  }
}
#import-calculator .title{
  font-size: 34px;
}
#import-calculator .loader {
  border: 4px solid #fff;
  border-top: 4px solid #fa4b47;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 2s linear infinite;
  display: inline-flex;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
#import-calculator .btn-red {
  background-color: #fa4b47;
  color: #fff;
  border-radius: 3px;
  font-size: 13px;
  padding: 13px 0;
  display: inline-block;
  text-decoration: none;
  width: 100%;
  text-align: center;
  height: fit-content;
}
#import-calculator .btn-red:hover {
  background-color: #3A5A8F;
}
#import-calculator .price {
  font-size: 32px;
  color: #FA4B48;
  padding: 10px 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
}
@font-face {
  font-family: 'Gilroy ExtraBold';
  src: local('Gilroy ExtraBold'), local('Gilroy-ExtraBold'),
  url('assets/fonts/Gilroy-ExtraBold.eot') format('eot'),
  url('assets/fonts/Gilroy-ExtraBold.woff') format('woff'),
  url('assets/fonts/Gilroy-ExtraBold.ttf') format('truetype');
  font-weight: 800;
  font-style: normal;
}
@font-face {
  font-family: 'Gilroy';
  src: local('Gilroy Regular'), local('Gilroy-Regular'),
  url('assets/fonts/Gilroy-Regular.woff2') format('woff2'),
  url('assets/fonts/Gilroy-Regular.woff') format('woff'),
  url('assets/fonts/Gilroy-Regular.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
}

#import-calculator {
  font-family: Gilroy;
  max-width: 1156px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

#import-calculator .title {
  font-style: normal;
  font-family: Gilroy ExtraBold,serif;
  line-height: 100%;
  color: #3A5A8F;
  flex: 1 0 100%;
}
#import-calculator .font16 {
  font-size: 16px;
}
#import-calculator .font18 {
  font-size: 18px;
}
#import-calculator .font14 {
  font-size: 14px;
}
#import-calculator .font20 {
  font-size: 20px;
}
#import-calculator .blue {
  color:#3A5A8F !important;
}
#import-calculator .red {
  color: #FA4B48 !important;
}
#import-calculator .bold{
  font-weight: 600 !important;
}
#import-calculator .extra-bold{
  font-family: Gilroy ExtraBold,serif;
}
#import-calculator .pb5 {
  padding-bottom: 5px !important
}
#import-calculator .p20{
  padding:20px !important;
}
.strike {
  text-decoration: line-through;
}
.nowrap {
  text-wrap: nowrap;
}
#import-calculator .left-block {
  flex-grow: 57;
  flex-basis: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 20px;
  white-space: nowrap;
  gap: 20px;
}

#import-calculator .row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}
#import-calculator .inline {
  flex-wrap: nowrap;
  gap: 10px;
  align-items: center;
}
#import-calculator .right-block {
  flex-grow: 43;
  flex-basis: 0;
  flex-wrap: wrap;
  white-space: normal;
  min-width: 330px;
  display: flex;
  gap: 12px;
  height: fit-content;
}

#import-calculator .calc-block {
  background: #F0F5FC;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  flex-grow: 1;
  flex-basis: 200px;
  height: fit-content;
  padding: 20px 12px;
}

#import-calculator .form-field {
  flex-grow: 1;
  position: relative;
  flex-basis: 0;
  min-width: 200px;
}

#import-calculator .form-label {
  white-space: normal;
  font-size: 16px;
  color: #3A5A8F;
  padding-bottom: 4px;
}

#import-calculator .form-field.small {
  min-width: auto;
}

#import-calculator .form-field.small .form-label {
  white-space: nowrap !important;
}

#import-calculator .form-input {
  display: flex;
  box-sizing: border-box;
  padding: 0 10px;
  height: 36px;
  border: 1px solid #CFCFCF;
  border-radius: 3px;
  color: #CFCFCF;
  font-family: Gilroy;
  font-size: 16px;
  position: relative;
  align-items: center;
}

#import-calculator .form-input .isset, .form-input.isset {
  color: #626262 !important;
}

#import-calculator .form-options {
  position: absolute;
  width: 100%;
  background-color: #f9f9f9;
  z-index: 1;
  color: #333333;
  max-height: 226px;
  overflow-y: auto;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
  padding: 5px;
  box-sizing: border-box;
}
#import-calculator .form-options ul{
  list-style: none;
  padding: 0;
  margin: 0;
}
#import-calculator .form-options li {
  padding: 5px 10px;
  cursor: pointer;
}
#import-calculator .form-options li:hover {
  background-color: #F0F5FD;
}

#import-calculator .form-input:after {
  content: "";
  border-style: solid;
  border-width: 6px 0 6px 6px;
  border-color: transparent transparent transparent #FA4B48;
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%) rotate(90deg);
}
#import-calculator .form-input.opened:after {
  transform: translateY(-50%) rotate(-90deg);
}
#import-calculator .selected-option.alt{
  border: 1px solid #FA4B48;
  line-height: 17px;
}

#import-calculator a {
  text-decoration: underline;
}
#import-calculator .circle {
  background-color: #49558B;
  cursor: pointer;
  display: inline-flex;
  align-items: end;
  justify-content: center;
  width: 1.2em;
  height: 1.2em;
  border-radius: 50%;
  font-size: inherit;
  color: white;
  line-height: 1;
  font-weight: 600;
}
#import-calculator .form-input::placeholder {
  color: #CFCFCF;
  opacity: 1;
}
#import-calculator .form-input:-ms-input-placeholder {
  color: #CFCFCF;
}
#import-calculator .form-input::-moz-placeholder {
  color: #CFCFCF;
  opacity: 1;
}
</style>
